import { useLocation, Routes, Route, Navigate, Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { SettingsCertificates } from './SettingsCertificates'
import { SettingsDrugs } from './SettingsDrugs'
import { SettingsUsers } from './SettingsUsers'
import { SettingsClinicHours } from './SettingsClinicHours'
import { SettingsAI } from './SettingsAI'
import { SettingsRolePermissions } from './SettingsRolePermissions'
import { SettingsConfidentiality } from './SettingsConfidentiality'
import { SettingsTenant } from './SettingsTenant'
import { OverrideRequestList } from '../components/OverrideRequestList'
import { SettingsPreferences } from './SettingsPreferences'
import { SettingsPracticeLocation } from './SettingsPracticeLocation'
import { SettingsAppointmentTypes } from './SettingsAppointmentTypes'
import { SettingsTaskAutomation } from './SettingsTaskAutomation'
import { SettingsPracticeVaccines } from './SettingsPracticeVaccines'
import { SettingsFormulary } from './SettingsFormulary'
import { SettingsDrugRecalls } from './SettingsDrugRecalls'
import { SettingsReminders } from './SettingsReminders'
import { SettingsProviders } from './SettingsProviders'
import { SettingsStc } from './SettingsStc'
import { SettingsPptPatientData } from './SettingsPptPatientData'
import { DrugRecallNew } from './DrugRecallNew'
import { DrugRecallDetail } from './DrugRecallDetail'
import { DataPortabilityExport } from './DataPortabilityExport'
import { useTenantProductType } from '../hooks/useTenantProductType'
import '../styles/navigation-tokens.css'
import './Settings.css'

export function Settings() {
  const location = useLocation()
  const { hasPermission, hasRole } = useAuth()
  const { isPptOnly } = useTenantProductType()

  // Determine active tab based on current route
  let activeTab = 'practice-location'
  if (location.pathname.includes('/practice-location')) {
    activeTab = 'practice-location'
  } else if (location.pathname.includes('/certificates')) {
    activeTab = 'certificates'
  } else if (location.pathname.includes('/drugs')) {
    activeTab = 'drugs'
  } else if (location.pathname.includes('/drug-recalls')) {
    activeTab = 'drug-recalls'
  } else if (location.pathname.includes('/reminders')) {
    activeTab = 'reminders'
  } else if (location.pathname.includes('/users')) {
    activeTab = 'users'
  } else if (location.pathname.includes('/role-permissions')) {
    activeTab = 'role-permissions'
  } else if (location.pathname.includes('/confidentiality')) {
    activeTab = 'confidentiality'
  } else if (location.pathname.includes('/emergency-overrides')) {
    activeTab = 'emergency-overrides'
  } else if (location.pathname.includes('/tenant')) {
    activeTab = 'tenant'
  } else if (location.pathname.includes('/clinic-hours')) {
    activeTab = 'clinic-hours'
  } else if (location.pathname.includes('/ai')) {
    activeTab = 'ai'
  } else if (location.pathname.includes('/preferences')) {
    activeTab = 'preferences'
  } else if (location.pathname.includes('/appointment-types')) {
    activeTab = 'appointment-types'
  } else if (location.pathname.includes('/task-automation')) {
    activeTab = 'task-automation'
  } else if (location.pathname.includes('/practice-vaccines')) {
    activeTab = 'practice-vaccines'
  } else if (location.pathname.includes('/formulary')) {
    activeTab = 'formulary'
  } else if (location.pathname.includes('/providers')) {
    activeTab = 'providers'
  } else if (location.pathname.includes('/data-export')) {
    activeTab = 'data-export'
  } else if (location.pathname.includes('/stc')) {
    activeTab = 'stc'
  } else if (location.pathname.includes('/ppt-patient-data')) {
    activeTab = 'ppt-patient-data'
  }

  return (
    <div className="settings" role="main">
      <div className="settings-header">
        <div>
          <h1>Settings</h1>
          <p className="settings-subtitle">Manage system configuration, certificates, and drug database</p>
        </div>
      </div>

      {/* Settings Container with Sidebar */}
      <div className="settings-container">
        {/* Vertical Sidebar Menu */}
        <nav className="settings-menu-sidebar" role="navigation" aria-label="Settings navigation">
          <ul className="settings-menu-list">
            <li>
              <Link
                to="/settings/practice-location"
                className={`settings-menu-item ${activeTab === 'practice-location' ? 'active' : ''}`}
              >
                <span className="settings-menu-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                    <polyline points="9 22 9 12 15 12 15 22"></polyline>
                  </svg>
                </span>
                <span className="settings-menu-label">Practice Location</span>
              </Link>
            </li>
            <li>
              <Link
                to="/settings/certificates"
                className={`settings-menu-item ${activeTab === 'certificates' ? 'active' : ''}`}
              >
                <span className="settings-menu-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                    <polyline points="10 9 9 9 8 9"></polyline>
                  </svg>
                </span>
                <span className="settings-menu-label">Certificates</span>
              </Link>
            </li>
            <li>
              <Link
                to="/settings/stc"
                className={`settings-menu-item ${activeTab === 'stc' ? 'active' : ''}`}
              >
                <span className="settings-menu-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 19l7-7 3 3-7 7-3-3z"></path>
                    <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"></path>
                    <path d="M2 2l7.586 7.586"></path>
                    <circle cx="11" cy="11" r="2"></circle>
                  </svg>
                </span>
                <span className="settings-menu-label">STC &amp; PCRS</span>
              </Link>
            </li>
            {isPptOnly && (
              <li>
                <Link
                  to="/settings/ppt-patient-data"
                  className={`settings-menu-item ${activeTab === 'ppt-patient-data' ? 'active' : ''}`}
                >
                  <span className="settings-menu-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="8.5" cy="7" r="4"></circle>
                      <polyline points="17 11 19 13 23 9"></polyline>
                    </svg>
                  </span>
                  <span className="settings-menu-label">Patients Data</span>
                </Link>
              </li>
            )}
            {!isPptOnly && (
            <li>
              <Link
                to="/settings/drugs"
                className={`settings-menu-item ${activeTab === 'drugs' ? 'active' : ''}`}
              >
                <span className="settings-menu-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="9" y1="3" x2="9" y2="21"></line>
                    <line x1="15" y1="3" x2="15" y2="21"></line>
                  </svg>
                </span>
                <span className="settings-menu-label">Drugs</span>
              </Link>
            </li>
            )}
            {!isPptOnly && hasPermission('drug-recalls:read') && (
              <li>
                <Link
                  to="/settings/drug-recalls"
                  className={`settings-menu-item ${activeTab === 'drug-recalls' ? 'active' : ''}`}
                >
                  <span className="settings-menu-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="12"></line>
                      <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                  </span>
                  <span className="settings-menu-label">Drug Recalls</span>
                </Link>
              </li>
            )}
            {!isPptOnly && (
            <li>
              <Link
                to="/settings/reminders"
                className={`settings-menu-item ${activeTab === 'reminders' ? 'active' : ''}`}
              >
                <span className="settings-menu-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"></path>
                    <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                  </svg>
                </span>
                <span className="settings-menu-label">Reminders</span>
              </Link>
            </li>
            )}
            {!isPptOnly && (
            <li>
              <Link
                to="/settings/providers"
                className={`settings-menu-item ${activeTab === 'providers' ? 'active' : ''}`}
              >
                <span className="settings-menu-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                    <line x1="8" y1="7" x2="18" y2="7"></line>
                    <line x1="8" y1="11" x2="18" y2="11"></line>
                    <line x1="8" y1="15" x2="14" y2="15"></line>
                  </svg>
                </span>
                <span className="settings-menu-label">Provider Directory</span>
              </Link>
            </li>
            )}
            <li>
              <Link
                to="/settings/users"
                className={`settings-menu-item ${activeTab === 'users' ? 'active' : ''}`}
              >
                <span className="settings-menu-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                </span>
                <span className="settings-menu-label">Users</span>
              </Link>
            </li>
            <li>
              <Link
                to="/settings/role-permissions"
                className={`settings-menu-item ${activeTab === 'role-permissions' ? 'active' : ''}`}
              >
                <span className="settings-menu-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"></path>
                  </svg>
                </span>
                <span className="settings-menu-label">Role Permissions</span>
              </Link>
            </li>
            {!isPptOnly && (
            <li>
              <Link
                to="/settings/confidentiality"
                className={`settings-menu-item ${activeTab === 'confidentiality' ? 'active' : ''}`}
              >
                <span className="settings-menu-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                  </svg>
                </span>
                <span className="settings-menu-label">Confidentiality</span>
              </Link>
            </li>
            )}
            {!isPptOnly && hasPermission('patients:override:emergency') && (
              <li>
                <Link
                  to="/settings/emergency-overrides"
                  className={`settings-menu-item ${activeTab === 'emergency-overrides' ? 'active' : ''}`}
                >
                  <span className="settings-menu-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
                      <line x1="12" y1="9" x2="12" y2="13"></line>
                      <line x1="12" y1="17" x2="12.01" y2="17"></line>
                    </svg>
                  </span>
                  <span className="settings-menu-label">Emergency Overrides</span>
                </Link>
              </li>
            )}
            <li>
              <Link
                to="/settings/tenant"
                className={`settings-menu-item ${activeTab === 'tenant' ? 'active' : ''}`}
              >
                <span className="settings-menu-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                    <path d="M9 22V12h6v10"></path>
                  </svg>
                </span>
                <span className="settings-menu-label">Tenant</span>
              </Link>
            </li>
            {!isPptOnly && (
            <li>
              <Link
                to="/settings/clinic-hours"
                className={`settings-menu-item ${activeTab === 'clinic-hours' ? 'active' : ''}`}
              >
                <span className="settings-menu-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                </span>
                <span className="settings-menu-label">Clinic Hours</span>
              </Link>
            </li>
            )}
            {!isPptOnly && (
            <li>
              <Link
                to="/settings/ai"
                className={`settings-menu-item ${activeTab === 'ai' ? 'active' : ''}`}
              >
                <span className="settings-menu-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2L2 7L12 12L22 7L12 2Z"></path>
                    <path d="M2 17L12 22L22 17"></path>
                    <path d="M2 12L12 17L22 12"></path>
                  </svg>
                </span>
                <span className="settings-menu-label">AI Configuration</span>
              </Link>
            </li>
            )}
            {!isPptOnly && (
            <li>
              <Link
                to="/settings/preferences"
                className={`settings-menu-item ${activeTab === 'preferences' ? 'active' : ''}`}
              >
                <span className="settings-menu-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3"></circle>
                    <path d="M12 1v6m0 6v6m9-9h-6m-6 0H3"></path>
                  </svg>
                </span>
                <span className="settings-menu-label">Preferences</span>
              </Link>
            </li>
            )}
            {!isPptOnly && (
            <li>
              <Link
                to="/settings/appointment-types"
                className={`settings-menu-item ${activeTab === 'appointment-types' ? 'active' : ''}`}
              >
                <span className="settings-menu-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                </span>
                <span className="settings-menu-label">Appointment Types</span>
              </Link>
            </li>
            )}
            {!isPptOnly && (
            <li>
              <Link
                to="/settings/task-automation"
                className={`settings-menu-item ${activeTab === 'task-automation' ? 'active' : ''}`}
              >
                <span className="settings-menu-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                  </svg>
                </span>
                <span className="settings-menu-label">Task Automation</span>
              </Link>
            </li>
            )}
            {!isPptOnly && (
            <li>
              <Link
                to="/settings/practice-vaccines"
                className={`settings-menu-item ${activeTab === 'practice-vaccines' ? 'active' : ''}`}
              >
                <span className="settings-menu-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="2" x2="12" y2="22"></line>
                    <line x1="2" y1="12" x2="22" y2="12"></line>
                    <circle cx="12" cy="12" r="10"></circle>
                  </svg>
                </span>
                <span className="settings-menu-label">Practice Vaccines</span>
              </Link>
            </li>
            )}
            {!isPptOnly && (
            <li>
              <Link
                to="/settings/formulary"
                className={`settings-menu-item ${activeTab === 'formulary' ? 'active' : ''}`}
              >
                <span className="settings-menu-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="9" y1="3" x2="9" y2="21"></line>
                    <line x1="15" y1="3" x2="15" y2="21"></line>
                    <line x1="3" y1="9" x2="21" y2="9"></line>
                    <line x1="3" y1="15" x2="21" y2="15"></line>
                  </svg>
                </span>
                <span className="settings-menu-label">Formulary</span>
              </Link>
            </li>
            )}
            {!isPptOnly && hasRole('admin') && (
              <li>
                <Link
                  to="/settings/data-export"
                  className={`settings-menu-item ${activeTab === 'data-export' ? 'active' : ''}`}
                >
                  <span className="settings-menu-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="7 10 12 15 17 10"></polyline>
                      <line x1="12" y1="15" x2="12" y2="3"></line>
                    </svg>
                  </span>
                  <span className="settings-menu-label">Data Export</span>
                </Link>
              </li>
            )}
          </ul>
        </nav>

        {/* Settings Content */}
        <div className="settings-content">
          <Routes>
            <Route path="practice-location" element={<SettingsPracticeLocation />} />
            <Route path="certificates" element={<SettingsCertificates />} />
            <Route path="stc" element={<SettingsStc />} />
            {isPptOnly && <Route path="ppt-patient-data" element={<SettingsPptPatientData />} />}
            {!isPptOnly && <Route path="drugs" element={<SettingsDrugs />} />}
            {!isPptOnly && <Route path="drug-recalls" element={<SettingsDrugRecalls />} />}
            {!isPptOnly && <Route path="drug-recalls/new" element={<DrugRecallNew />} />}
            {!isPptOnly && <Route path="drug-recalls/:id" element={<DrugRecallDetail />} />}
            {!isPptOnly && <Route path="drug-recalls/:id/affected-patients" element={<DrugRecallDetail />} />}
            {!isPptOnly && <Route path="reminders" element={<SettingsReminders />} />}
            {!isPptOnly && <Route path="providers" element={<SettingsProviders />} />}
            <Route path="users" element={<SettingsUsers />} />
            <Route path="role-permissions" element={<SettingsRolePermissions />} />
            {!isPptOnly && <Route path="confidentiality" element={<SettingsConfidentiality />} />}
            {!isPptOnly && <Route path="emergency-overrides" element={<OverrideRequestList />} />}
            <Route path="tenant" element={<SettingsTenant />} />
            {!isPptOnly && <Route path="clinic-hours" element={<SettingsClinicHours />} />}
            {!isPptOnly && <Route path="ai" element={<SettingsAI />} />}
            {!isPptOnly && <Route path="preferences" element={<SettingsPreferences />} />}
            {!isPptOnly && <Route path="appointment-types" element={<SettingsAppointmentTypes />} />}
            {!isPptOnly && <Route path="task-automation" element={<SettingsTaskAutomation />} />}
            {!isPptOnly && <Route path="practice-vaccines" element={<SettingsPracticeVaccines />} />}
            {!isPptOnly && <Route path="formulary" element={<SettingsFormulary />} />}
            {!isPptOnly && <Route path="data-export" element={<DataPortabilityExport />} />}
            <Route index element={<Navigate to="/settings/practice-location" replace />} />
            <Route path="*" element={<Navigate to="/settings/practice-location" replace />} />
          </Routes>
        </div>
      </div>
    </div>
  )
}

